import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, LockKeyhole, Mail, ArrowRight, Smartphone, Share2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // Safari-safe detection: run in effect, wrapped in try/catch (Private Browsing throws)
  useEffect(() => {
    try {
      if (typeof navigator !== "undefined") {
        setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent || ""));
      }
      if (typeof window !== "undefined") {
        const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)");
        setIsStandalone((mm && mm.matches) || window.navigator.standalone === true);
      }
    } catch (e) {
      console.error("Install detection failed:", e);
    }
  }, []);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Bun venit, ${res?.user?.name || ""}`);
      nav("/", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 noise-bg">
      {/* Left: brand panel — minimal, only logo + gold rings */}
      <aside className="hidden lg:flex flex-col items-center justify-center p-12 bg-[#0F0D0A] text-white relative overflow-hidden">
        <div className="relative z-10 flex flex-col items-center gap-6">
          <img
            src="/logo.png"
            alt="IMAGGIO 2020 TIM"
            className="w-72 h-72 object-contain drop-shadow-[0_4px_20px_rgba(201,169,110,0.45)]"
          />
          <div className="text-center">
            <p className="font-brand text-3xl leading-tight" style={{ color: "#D4B77E", letterSpacing: "0.14em" }}>
              IMAGGIO
            </p>
            <p className="text-xs uppercase tracking-[0.4em] text-white/40 mt-2">2020 TIM SRL</p>
          </div>
        </div>

        {/* Concentric gold rings echoing the "O" of IMAGGIO */}
        <div
          className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full pointer-events-none opacity-25"
          style={{
            background: "radial-gradient(circle, transparent 55%, rgba(201,169,110,0.35) 56%, rgba(201,169,110,0.35) 57%, transparent 58%, transparent 63%, rgba(201,169,110,0.25) 64%, rgba(201,169,110,0.25) 65%, transparent 66%, transparent 71%, rgba(201,169,110,0.18) 72%, rgba(201,169,110,0.18) 73%, transparent 74%)",
          }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full pointer-events-none opacity-15"
          style={{
            background: "radial-gradient(circle, transparent 55%, rgba(201,169,110,0.35) 56%, rgba(201,169,110,0.35) 57%, transparent 58%, transparent 63%, rgba(201,169,110,0.25) 64%, rgba(201,169,110,0.25) 65%, transparent 66%)",
          }}
        />
      </aside>

      {/* Right: form */}
      <main className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="IMAGGIO 2020 TIM" className="w-12 h-12 object-contain" />
            <div>
              <p className="font-brand text-lg leading-tight text-gold-deep" style={{ letterSpacing: "0.08em" }}>IMAGGIO</p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-0.5">2020 TIM SRL</p>
            </div>
          </div>

          <h2 className="font-display font-bold text-4xl tracking-tight">Intră în cont</h2>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="label-eyebrow">Email</Label>
              <div className="relative mt-2">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11 bg-white"
                  required
                  data-testid="login-email-input"
                  placeholder="admin@imaggio2020tm.ro"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="label-eyebrow">Parolă</Label>
              <div className="relative mt-2">
                <LockKeyhole className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-11 bg-white"
                  required
                  data-testid="login-password-input"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 btn-gold rounded-md text-base group"
              data-testid="login-submit-btn"
            >
              {submitting ? "Se autentifică..." : (<><span>Autentificare</span> <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" /></>)}
            </Button>
          </form>

          {/* Install on phone banner — always visible on Login */}
          {!isStandalone && (
            <div className="mt-8 pt-6 border-t border-border">
              <button
                type="button"
                onClick={() => setShowInstallGuide(true)}
                className="w-full flex items-center gap-3 p-4 rounded-md border-2 border-dashed border-[hsl(var(--gold))] bg-[hsl(var(--gold-soft))]/50 hover:bg-[hsl(var(--gold-soft))] transition-colors text-left"
                data-testid="login-install-app-btn"
              >
                <Smartphone className="w-6 h-6 text-[hsl(var(--gold-deep))] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[hsl(var(--charcoal))]">
                    Instalează aplicația pe telefon
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isIOS
                      ? "Safari → Share → Adaugă la ecranul principal"
                      : "Se instalează ca aplicație nativă — offline & rapid"}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-[hsl(var(--gold-deep))] shrink-0" />
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Install guide modal */}
      <Dialog open={showInstallGuide} onOpenChange={setShowInstallGuide}>
        <DialogContent className="max-w-md" data-testid="install-guide-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Smartphone className="w-6 h-6 text-[hsl(var(--gold-deep))]" />
              Instalează pe {isIOS ? "iPhone" : "telefon"}
            </DialogTitle>
            <DialogDescription>
              Aplicația se instalează ca app nativă, fără App Store. Ocupă ~2 MB.
            </DialogDescription>
          </DialogHeader>

          {isIOS ? (
            <ol className="space-y-4 text-sm">
              <li className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--gold))] text-white flex items-center justify-center font-bold text-xs">1</span>
                <div>
                  <p className="font-semibold">Deschide în Safari</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Trebuie neapărat Safari, NU Chrome sau alt browser pe iPhone.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--gold))] text-white flex items-center justify-center font-bold text-xs">2</span>
                <div>
                  <p className="font-semibold flex items-center gap-1.5">Apasă butonul <Share2 className="w-4 h-4 inline text-blue-500" /> Share</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Se află jos-centru pe iPhone (pătrat cu săgeată în sus).</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--gold))] text-white flex items-center justify-center font-bold text-xs">3</span>
                <div>
                  <p className="font-semibold flex items-center gap-1.5">Scroll jos → <Plus className="w-4 h-4 inline" /> „Adaugă la ecranul principal"</p>
                  <p className="text-xs text-muted-foreground mt-0.5">În engleză: „Add to Home Screen".</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--gold))] text-white flex items-center justify-center font-bold text-xs">4</span>
                <div>
                  <p className="font-semibold">Confirmă „Adaugă" / „Add"</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Iconița IMAGGIO apare pe home screen. Deschide-o și te autentifici o singură dată.</p>
                </div>
              </li>
            </ol>
          ) : (
            <ol className="space-y-4 text-sm">
              <li className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--gold))] text-white flex items-center justify-center font-bold text-xs">1</span>
                <div>
                  <p className="font-semibold">Deschide în Chrome / Edge</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--gold))] text-white flex items-center justify-center font-bold text-xs">2</span>
                <div>
                  <p className="font-semibold">Meniul (⋮) sus-dreapta → „Instalează aplicația"</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Sau apare automat un banner „Adaugă IMAGGIO".</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--gold))] text-white flex items-center justify-center font-bold text-xs">3</span>
                <div>
                  <p className="font-semibold">Confirmă „Instalează"</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Iconița apare pe ecranul principal / desktop.</p>
                </div>
              </li>
            </ol>
          )}

          <div className="mt-4 p-3 bg-slate-50 rounded-md text-xs text-slate-600">
            💡 După instalare, aplicația funcționează și offline (citire) și primești notificări dacă sunt activate.
          </div>

          {/* QR code — for scanning from a phone when this dialog is opened on desktop */}
          <div className="mt-4 border-t pt-4">
            <p className="text-xs font-semibold text-slate-700 mb-2 text-center">
              Sau scanează cu telefonul (Camera / Safari):
            </p>
            <div className="flex flex-col items-center gap-2">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=4&data=${encodeURIComponent(typeof window !== "undefined" ? window.location.origin + "/login" : "")}`}
                alt="QR install"
                className="w-52 h-52 border border-slate-200 rounded-md bg-white p-1"
                data-testid="install-qr-code"
              />
              <p className="text-[10px] text-slate-500 break-all text-center max-w-xs">
                {typeof window !== "undefined" ? window.location.origin + "/login" : ""}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
